import {
  handleAddToCart,
  handleRemoveFromCart,
  isInBulkCart,
} from "@/utils/helpers";
import { AnimatedQuantity } from "@/utils/numberFlow";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { HomeProduct } from "@/types/home";
import { LoaderState } from "@/types/cart";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { trackEvent } from "@/lib/gtag";

interface AddToCartProps {
  product: HomeProduct;
  currentQuantity: number;
  availableQty?: number;
  selected: "ndp" | "sales";
}

const AddToCart: React.FC<AddToCartProps> = ({
  product,
  availableQty,
  currentQuantity,
  selected,
}) => {
  const dispatch = useAppDispatch();
  const { phoneNumber } = useAppSelector((state) => state.auth);
  const { cart } = useAppSelector((state) => state.cart);
  const { selectedSKU } = useAppSelector((state) => state.home);
  const [open, setOpen] = useState<LoaderState | null>(null);

  // ---

  const handleOpen = (sku: string, type: string) =>
    setOpen({ sku: sku, type: type });
  const handleClose = () => setOpen(null);

  const handleAddCarts = (data: HomeProduct, type: string) => {
    const skuId = selectedSKU ?? data?.SKU_ID;
    const bundleQty = parseInt(data?.Bundle) || 1;
    const available = availableQty ?? product?.Available_quantity ?? 0;

    let newQty = currentQuantity;

    if (type === "inc") {
      if (available <= currentQuantity) return;

      if (selected === "ndp") {
        if (newQty + 1 <= available) {
          newQty += 1;
        } else {
          return;
        }
      } else if (selected === "sales") {
        const remainder = currentQuantity % bundleQty;
        const increment = remainder === 0 ? bundleQty : bundleQty - remainder;
        if (newQty + increment <= available) {
          newQty += increment;
        } else {
          return;
        }
      } else {
        // default case
        if (newQty + bundleQty <= available) {
          newQty += bundleQty;
        } else {
          return;
        }
      }
    }

    if (type === "dec") {
      if (selected === "ndp") {
        newQty = Math.max(0, newQty - 1);
      } else if (selected === "sales") {
        const remainder = newQty % bundleQty;
        newQty =
          remainder === 0
            ? Math.max(0, newQty - bundleQty)
            : Math.max(0, newQty - remainder);
      } else {
        newQty = Math.max(0, newQty - bundleQty);
      }

      // avoid updating cart if quantity is 0 after decrement
      if (newQty === 0) return;
    }

    handleOpen(skuId, type);

    trackEvent({
      action: type === "inc" ? "Increase Quantity" : "Decrease Quantity",
      category: "Product",
      label: skuId,
    });

    handleAddToCart(
      dispatch,
      skuId,
      phoneNumber,
      newQty,
      selected,
      handleClose
    );
  };

  return (
    <Box
      position="sticky"
      bottom={0}
      left={0}
      width="100%"
      py={2}
      px={2}
      zIndex={2}
      bgcolor="white"
      boxShadow="2px 0px 4px rgba(0, 0, 0, 0.2)"
    >
      <Box
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <span className="text-[18px] font-bold">Quantity:</span>
        <div className="flex items-center justify-center">
          {/* Decrease Button */}
          <Button
            variant="contained"
            size="small"
            sx={{
              width: "75px",
              height: "40px",
              fontSize: "25px",
              minWidth: "unset",
              boxShadow: "none",
              borderRadius: "4px 0 0 4px",
              backgroundColor:
                currentQuantity > 0 ? "var(--color-green)" : "#a2deb1",
              color: "#fff",
              "&:hover": {
                backgroundColor:
                  currentQuantity > 0 ? "var(--color-green-hover)" : "#a2deb1",
              },
              cursor: currentQuantity > 0 ? "pointer" : "not-allowed",
            }}
            onClick={() => {
              if (currentQuantity === Number(product?.Bundle)) {
                handleOpen(selectedSKU ?? product?.SKU_ID, "dec");
                handleRemoveFromCart(
                  dispatch,
                  selectedSKU ?? product?.SKU_ID,
                  phoneNumber,
                  handleClose
                );
              } else {
                handleAddCarts(product, "dec");
              }
            }}
          >
            {open?.sku === (selectedSKU ?? product?.SKU_ID) &&
            open?.type === "dec" ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "-"
            )}
          </Button>

          {/* Quantity Display */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="#e3e3e3"
            width="75px"
            height={40}
          >
            <Typography
              variant="subtitle1"
              fontSize={15}
              sx={{ margin: "0 auto" }}
            >
              <AnimatedQuantity
                value={
                  isInBulkCart(cart, selectedSKU ?? product?.SKU_ID)
                    ? currentQuantity
                    : 0
                }
              />
            </Typography>
          </Box>

          {/* Increase Button */}
          <Button
            variant="contained"
            size="small"
            sx={{
              width: "75px",
              height: "40px",
              fontSize: "25px",
              minWidth: "unset",
              borderRadius: "0 4px 4px 0",
              boxShadow: "none",
              backgroundColor:
                ((selected === "sales" && (availableQty ?? product?.Available_quantity) >= Number(product?.Bundle)) && currentQuantity < (availableQty ?? product?.Available_quantity)) || 
                (selected === "ndp" && (availableQty ?? product?.Available_quantity) > 0 && currentQuantity < (availableQty ?? product?.Available_quantity))
                  ? "var(--color-green)"
                  : "#a2deb1",
              color: "#fff",
              "&:hover": {
                backgroundColor:
                  ((selected === "sales" && (availableQty ?? product?.Available_quantity) >= Number(product?.Bundle)) && currentQuantity < (availableQty ?? product?.Available_quantity)) || 
                  (selected === "ndp" && (availableQty ?? product?.Available_quantity) > 0 && currentQuantity < (availableQty ?? product?.Available_quantity))
                    ? "var(--color-green-hover)"
                    : "#a2deb1",
              },
              cursor:
                ((selected === "sales" && (availableQty ?? product?.Available_quantity) >= Number(product?.Bundle)) && currentQuantity < (availableQty ?? product?.Available_quantity)) || 
                (selected === "ndp" && (availableQty ?? product?.Available_quantity) > 0 && currentQuantity < (availableQty ?? product?.Available_quantity))
                  ? "pointer"
                  : "not-allowed",
            }}
            onClick={() => {
              if (currentQuantity === 0) {
                handleAddCarts(product, "inc");
              } else if (
                currentQuantity < (availableQty ?? product?.Available_quantity)
              ) {
                handleAddCarts(product, "inc");
              }
            }}
          >
            {open?.sku === (selectedSKU ?? product?.SKU_ID) &&
            open?.type === "inc" ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "+"
            )}
          </Button>

          {/* Reset Button */}
          <IconButton
            sx={{
              color: "var(--color-gray)",
              width: "15%",
              height: "40px",
              "&:hover": {
                color: "gray",
              },
            }}
            aria-label="Reset quantity"
          >
            <CloseIcon
              style={{ fontSize: "22px" }}
              className={`${
                isInBulkCart(cart, selectedSKU ?? product?.SKU_ID)
                  ? "text-peach cursor-pointer"
                  : "text-peach/30 cursor-not-allowed"
              }`}
              onClick={() =>
                isInBulkCart(cart, selectedSKU ?? product?.SKU_ID) &&
                handleRemoveFromCart(
                  dispatch,
                  selectedSKU ?? product?.SKU_ID,
                  phoneNumber
                )
              }
            />
          </IconButton>
        </div>
      </Box>
    </Box>
  );
};

export default AddToCart;
